import { redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import { Result } from "better-result";
import { z } from "zod/v4";
import { getClassesForStartLesson } from "#/modules/school.klassen/api.klassen.server";
import {
  addVereniging,
  getLeerlingSportdeelname,
  getSportdeelnameStatistieken,
  getSportdeelnameStatistiekLeerlingen,
  getSportdeelnameTrend,
  removeVereniging,
  updateFietsvaardigheid,
  updateHasNoSport,
  updateMrt,
  updateSportdeelnameSport,
  updateSportdeelnameVereniging,
  updateSportdeelnameWensSport,
  updateZwemvaardigheid,
} from "#/modules/sportdeelname/api.sportdeelname.server";
import { effectiveKlasIdsForStatistieken } from "#/modules/sportdeelname/filters.sportdeelname-statistieken";
import type { SportdeelnameStatistiekenFilters } from "#/modules/sportdeelname/schema.search";
import { isSportdeelnameStatKpi } from "#/modules/sportdeelname/sportdeelname-stat-kpi";
import { apiFailure } from "#/modules/util.errors/api-failure.server";

export const getSportParticipationStats = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: { filters: SportdeelnameStatistiekenFilters }) => data)
  .handler(async ({ data }) => {
    const { filters } = data;
    const klassenResult = await getClassesForStartLesson();
    if (Result.isError(klassenResult)) throw klassenResult.error;
    const klassen = klassenResult.value.data ?? [];

    if (klassen.length === 0) {
      return { klassen, stats: null, trend: null };
    }

    const allIds = klassen.map((k) => k.id);
    const effectiveKlasIds = effectiveKlasIdsForStatistieken(filters, allIds);

    const [result, trendResult] = await Promise.all([
      getSportdeelnameStatistieken(effectiveKlasIds),
      getSportdeelnameTrend(effectiveKlasIds),
    ]);
    if (Result.isError(result)) {
      const err = result.error;
      throw err;
    }
    if (Result.isError(trendResult)) {
      throw trendResult.error;
    }

    return { klassen, stats: result.value, trend: trendResult.value };
  });

export const getSportParticipationKpiLeerlingen = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(
    (data: {
      filters: SportdeelnameStatistiekenFilters;
      kpi: string;
      locale: string;
      school: string;
      schoolyear?: string;
    }) => data,
  )
  .handler(async ({ data }) => {
    const { filters, locale, school, schoolyear } = data;
    const backToParent = {
      to: "/$locale/{-$schoolyear}/$school/insights/sport-participation",
      params: { locale, schoolyear, school },
      search: filters,
    } as const;

    if (!isSportdeelnameStatKpi(data.kpi)) {
      throw redirect(backToParent);
    }

    const kpi = data.kpi;
    const klassenResult = await getClassesForStartLesson();
    if (Result.isError(klassenResult)) throw klassenResult.error;
    const klassen = klassenResult.value.data ?? [];

    if (klassen.length === 0) {
      throw redirect(backToParent);
    }

    const allIds = klassen.map((k) => k.id);
    const effectiveKlasIds = effectiveKlasIdsForStatistieken(filters, allIds);

    const result = await getSportdeelnameStatistiekLeerlingen(kpi, effectiveKlasIds);
    if (Result.isError(result)) {
      const err = result.error;
      throw err;
    }

    return { kpi, data: result.value };
  });

export const getSportParticipationLeerling = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: { leerlingId: string }) => data)
  .handler(async ({ data }) => {
    const result = await getLeerlingSportdeelname(data.leerlingId);
    if (Result.isError(result)) {
      throw result.error;
    }
    return { data: result.value };
  });

const sportToggleSchema = z.object({
  leerlingId: z.uuid(),
  sportId: z.uuid(),
  value: z.boolean(),
});

export const submitUpdateSport = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(sportToggleSchema)
  .handler(async ({ data }) => {
    const result = await updateSportdeelnameSport(data.leerlingId, {
      sportId: data.sportId,
      value: data.value,
    });
    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

export const submitUpdateVereniging = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(z.object({ leerlingId: z.uuid(), verenigingId: z.uuid(), value: z.boolean() }))
  .handler(async ({ data }) => {
    const result = await updateSportdeelnameVereniging(data.leerlingId, {
      verenigingId: data.verenigingId,
      value: data.value,
    });
    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

export const submitUpdateWensSport = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(sportToggleSchema)
  .handler(async ({ data }) => {
    const result = await updateSportdeelnameWensSport(data.leerlingId, {
      sportId: data.sportId,
      value: data.value,
    });
    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

export const submitUpdateMrt = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(
    z.object({
      leerlingId: z.uuid(),
      mrt: z.object({
        mrtLes: z.boolean(),
        fysiotherapie: z.boolean(),
        tweedeLijn: z.boolean(),
      }),
    }),
  )
  .handler(async ({ data }) => {
    const result = await updateMrt(data.leerlingId, { mrt: data.mrt });
    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

export const submitUpdateZwemvaardigheid = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(
    z.object({
      leerlingId: z.uuid(),
      zwemvaardigheid: z.object({
        geenDiploma: z.boolean(),
        diplomaA: z.boolean(),
        diplomaB: z.boolean(),
        diplomaC: z.boolean(),
        inProgress: z.boolean(),
        wachtlijst: z.boolean(),
      }),
    }),
  )
  .handler(async ({ data }) => {
    const result = await updateZwemvaardigheid(data.leerlingId, {
      zwemvaardigheid: data.zwemvaardigheid,
    });
    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

export const submitUpdateFietsvaardigheid = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(
    z.object({
      leerlingId: z.uuid(),
      fietsvaardigheid: z.object({
        ikKanFietsen: z.boolean(),
        inProgress: z.boolean(),
        kanNietFietsen: z.boolean(),
      }),
    }),
  )
  .handler(async ({ data }) => {
    const result = await updateFietsvaardigheid(data.leerlingId, {
      fietsvaardigheid: data.fietsvaardigheid,
    });
    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

export const submitUpdateHasNoSport = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(z.object({ leerlingId: z.uuid(), hasNoSport: z.boolean() }))
  .handler(async ({ data }) => {
    const result = await updateHasNoSport(data.leerlingId, {
      hasNoSport: data.hasNoSport,
      leerlingId: data.leerlingId,
    });
    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

export const submitAddVereniging = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(z.object({ leerlingId: z.uuid(), sportId: z.uuid(), name: z.string().trim().min(1) }))
  .handler(async ({ data }) => {
    const result = await addVereniging({ name: data.name, sportId: data.sportId });
    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
    // Backend is get-or-create: it returns the id of the new OR already-existing
    // vereniging, so we can immediately select it for the leerling. Selecting an
    // already-selected vereniging 500s (duplicate row, devops 887), so skip it then.
    const { id, alreadyExists } = result.value;
    if (alreadyExists) {
      const current = await getLeerlingSportdeelname(data.leerlingId);
      if (Result.isError(current)) {
        return apiFailure(current.error);
      }
      const selection = current.value?.sportdeelname.verenigingen.find((v) => v.id === id);
      if (selection?.selected) {
        return { alreadyExists, wasAlreadySelected: true };
      }
    }
    const selectResult = await updateSportdeelnameVereniging(data.leerlingId, {
      verenigingId: id,
      value: true,
    });
    if (Result.isError(selectResult)) {
      return apiFailure(selectResult.error);
    }
    return { alreadyExists, wasAlreadySelected: false };
  });

export const submitRemoveVereniging = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(z.object({ verenigingId: z.uuid() }))
  .handler(async ({ data }) => {
    const result = await removeVereniging({ verenigingId: data.verenigingId });
    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });
